import type { DataProvider, GetListParams, GetOneParams } from 'react-admin';

import { requirePartner } from '@src/providers/dataProvider/shared';

import { fetchOrders } from './fetchOrders';
import type { FetchOrdersFilters, OrderRecord, TriState } from './types';

// The list filter values react-admin threads through (it types `filter` as
// `any`); narrowing here keeps the mapping below type-checked. Without it a
// renamed `source` — or a scalar that disagrees with the schema — compiles.
interface OrderFilter {
  state?: string;
  ypackageState?: string;
  orderNumber?: string;
  orderId?: string | number;
  orderUuid?: string;
  externalOrderNumber?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  captured?: string;
  hasReturns?: string;
  hasEscalations?: string;
  commercePlatform?: string;
}

// Drop blank text filters. react-admin leaves a cleared input as '' rather than removing the
// key, and an empty filter is not a filter — the server ignores a falsy value, so forwarding one
// is noise. Trimming matters too: "  " is truthy, so it used to be sent as-is and matched
// nothing, which reads as "no results" rather than "no filter".
const text = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed === '' ? undefined : trimmed;
};

// Coerce a free-text numeric filter, dropping blanks and non-numeric input.
// Without the NaN guard a typo reaches the API as `null`, which the server
// discards — so the list comes back unfiltered and reads as a match.
const toNumber = (value: string | number | undefined): number | undefined => {
  if (value == null || value === '') return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

// The yes/no SelectInputs carry 'true'/'false'; the API wants its tri-state Int
// (1 = yes, 2 = no). Mapping 'false' to 2 rather than 0 matters: the resolver
// tests `=== 1` / `=== 2`, so a 0 matches neither branch and is ignored —
// every order comes back while the chip still claims a "No" search applied.
const toTriState = (value: string | undefined): TriState | undefined => {
  if (value === 'true') return 1;
  if (value === 'false') return 2;
  return undefined;
};

const toApiFilters = (params: GetListParams): FetchOrdersFilters => {
  const { page = 1, perPage = 25 } = params.pagination ?? {};
  const filter = (params.filter ?? {}) as OrderFilter;
  // reware-api's orders query takes only 'newest'/'oldest' (placed date); the
  // sort *field* is ignored, which is why every column but Placed is
  // sortable={false} in OrderList.
  const sort = params.sort?.order === 'ASC' ? 'oldest' : 'newest';

  return {
    partnerId: requirePartner().id,
    page,
    pageSize: perPage,
    sort,
    state: text(filter.state),
    ypackageState: text(filter.ypackageState),
    orderNumber: text(filter.orderNumber),
    orderId: toNumber(filter.orderId),
    orderUuid: text(filter.orderUuid),
    externalOrderNumber: text(filter.externalOrderNumber),
    email: text(filter.email),
    firstName: text(filter.firstName),
    lastName: text(filter.lastName),
    captured: toTriState(filter.captured),
    // No refundState here — no input supplies one. If one is ever added, note the
    // server turns it into a *required* Refund join, so an unrecognised value
    // returns an empty page rather than being ignored.
    hasReturns: toTriState(filter.hasReturns),
    hasEscalations: toTriState(filter.hasEscalations),
    commercePlatform: text(filter.commercePlatform),
  };
};

/**
 * Read-only provider: only getList and getOne are supported. The remaining
 * DataProvider methods are stubbed to throw so an unsupported call fails with a
 * clear message rather than an opaque "undefined is not a function" (same stub
 * pattern as src/giftCards/dataProvider.ts).
 */
interface OrdersDataProvider {
  getList: (
    resource: string,
    params: GetListParams
  ) => Promise<{ data: OrderRecord[], total: number }>;
  getOne: (
    resource: string,
    params: GetOneParams
  ) => Promise<{ data: OrderRecord }>;
  getMany: () => Promise<never>;
  getManyReference: () => Promise<never>;
  create: () => Promise<never>;
  update: () => Promise<never>;
  updateMany: () => Promise<never>;
  delete: () => Promise<never>;
  deleteMany: () => Promise<never>;
}

const ordersDataProviderImpl: OrdersDataProvider = {
  getList: async (_resource, params) => {
    const result = await fetchOrders(toApiFilters(params));
    return { data: result.rows, total: result.count };
  },

  getOne: async (_resource, params) => {
    // Validate before querying. A NaN id serializes as null, which the server drops, leaving an
    // unfiltered query — so the partner's newest order would come back, pass the not-found check
    // below, and be returned as the record that was asked for, with its customer PII.
    const orderId = Number(params.id);
    if (!Number.isInteger(orderId)) {
      throw new Error(`Order ${params.id} not found`);
    }

    const result = await fetchOrders({
      partnerId: requirePartner().id,
      page: 1,
      pageSize: 1,
      sort: 'newest',
      orderId,
    });
    if (!result.rows.length) {
      throw new Error(`Order ${params.id} not found`);
    }
    return { data: result.rows[0] };
  },

  getMany: async () => { throw new Error('getMany not implemented for orders resource'); },
  getManyReference: async () => { throw new Error('getManyReference not implemented for orders resource'); },
  create: async () => { throw new Error('create not implemented for orders resource'); },
  update: async () => { throw new Error('update not implemented for orders resource'); },
  updateMany: async () => { throw new Error('updateMany not implemented for orders resource'); },
  delete: async () => { throw new Error('delete not implemented for orders resource'); },
  deleteMany: async () => { throw new Error('deleteMany not implemented for orders resource'); },
};

// React-admin's DataProvider methods are generic, which prevents a type-safe
// single-resource provider; we define proper types above and cast here.
export const ordersDataProvider = ordersDataProviderImpl as unknown as DataProvider;
